import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./quickActions.css";
import "../finance/finance.css";

const ROLE_BY_LIST_SOURCE = {
    guide: "guide",
    cafe: "cafe",
    driver4x4: "driver",
    driverbus: "driver",
    driversedan: "driver",
    driver4x4other: "driver",
    cars: "vehicle",
};

const LIST_SOURCE_LABELS = {
    guide: "Гид",
    cafe: "Кафе",
    driver4x4: "Водитель 4x4",
    driverbus: "Водитель автобуса",
    driversedan: "Водитель седана",
    driver4x4other: "Водитель 4x4 (проч.)",
    cars: "Авто",
};

const fmtTill = (n) => {
    const v = Number(n);
    if (!v) return "0.00";
    return v.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* ────────────────────────────────────────────────────────────
 *  ПРИХОД — куда зачислить + сумма + комментарий (обязателен,
 *  это единственная "память" о том, откуда деньги)
 * ──────────────────────────────────────────────────────────── */
function QuickIncomeForm({ API_URL, paymentTypes = [], kassaList = [], onSaved, onClose }) {
    const [target, setTarget] = useState("");
    const [amount, setAmount] = useState("");
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);

    const regularTypes = paymentTypes.filter((pt) => !pt.isCard && pt.showInIncome);
    const visibleKassa = kassaList.filter((k) => k.showInIncome);

    const targetCurrency = useMemo(() => {
        if (!target) return null;
        const [kind, id] = target.split(":");
        if (kind === "k") return visibleKassa.find((k) => k.id === Number(id))?.currencyCode ?? null;
        return regularTypes.find((pt) => pt.id === Number(id))?.currencyCode ?? null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target]);

    const submit = async () => {
        if (!target) { toast.error("Выберите кассу"); return; }
        if (!amount || Number(amount) <= 0) { toast.error("Укажите сумму"); return; }
        if (!comment.trim()) { toast.error("Добавьте комментарий — откуда деньги"); return; }
        const [kind, id] = target.split(":");
        setSaving(true);
        try {
            await axios.post(`${API_URL}/addKassaIncome`, {
                paymentTypeId: kind === "pt" ? Number(id) : null,
                kassaId: kind === "k" ? Number(id) : null,
                amount, source: comment,
            });
            toast.success("Приход добавлен");
            onSaved();
            onClose();
        } catch (err) {
            console.error("addKassaIncome:", err);
            toast.error("Не удалось сохранить");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="qa-form">
            <div className="qa-till">
                <div className="qa-till-row">
                    <span className="qa-till-label">Приход</span>
                    <span className="qa-till-currency">{targetCurrency || "—"}</span>
                </div>
                <div className="qa-till-amount">+{fmtTill(amount)}</div>
            </div>

            <div className="qa-tear" />

            <div className="qa-field">
                <label>Куда зачислить</label>
                {regularTypes.length === 0 && visibleKassa.length === 0 ? (
                    <div className="qa-chip-empty">Нет доступных касс — включи хотя бы одну в «Приход» на странице Кассы</div>
                ) : (
                    <div className="qa-chip-row">
                        {regularTypes.map((pt) => (
                            <button
                                type="button"
                                key={`pt-${pt.id}`}
                                className={`qa-chip${target === `pt:${pt.id}` ? " active" : ""}`}
                                onClick={() => setTarget(`pt:${pt.id}`)}
                            >
                                {pt.name}
                            </button>
                        ))}
                        {visibleKassa.map((k) => (
                            <button
                                type="button"
                                key={`k-${k.id}`}
                                className={`qa-chip${target === `k:${k.id}` ? " active" : ""}`}
                                onClick={() => setTarget(`k:${k.id}`)}
                            >
                                {paymentTypes.find((pt) => pt.id === k.paymentTypeId)?.name} — {k.recipient}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="qa-field">
                <label>Сумма</label>
                <input type="number" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>

            <div className="qa-field">
                <label>Комментарий <span className="qa-req">— откуда пришли деньги</span></label>
                <textarea rows={2} placeholder="Например: продажа сувениров, возврат от поставщика…" value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>

            <button className="qa-submit income" onClick={submit} disabled={saving}>
                {saving ? "Сохраняем…" : (<><i className="fa-solid fa-check"></i> Добавить приход</>)}
            </button>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────
 *  РАСХОД — можно оплатить сразу или только запланировать.
 *  У категории может быть несколько listSource через запятую
 *  ("guide,cafe") — каждое имя в выпадающем списке "помнит"
 *  свой source, чтобы при сохранении взять правильный role.
 * ──────────────────────────────────────────────────────────── */
function QuickExpenseForm({ API_URL, categories = [], paymentTypes = [], kassaList = [], currencies = [], onSaved, onClose }) {
    const [isPlan, setIsPlan] = useState(false);
    const [title, setTitle] = useState("");
    const [categoryId, setCategoryId] = useState(null);
    const [personSelection, setPersonSelection] = useState(""); // формат "source::Имя"
    const [personOptions, setPersonOptions] = useState([]);     // [{name, source}]
    const [loadingPersons, setLoadingPersons] = useState(false);
    const [paymentTypeId, setPaymentTypeId] = useState(null);
    const [kassaId, setKassaId] = useState(null);
    const [planCurrency, setPlanCurrency] = useState("USD");
    const [amount, setAmount] = useState("");
    const [comment, setComment] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [saving, setSaving] = useState(false);

    const mainCats = categories.filter((c) => !c.parentId);
    const subsOf = (id) => categories.filter((c) => c.parentId === id);

    const visibleTypes = paymentTypes.filter((pt) => pt.showInExpense);
    const visibleKassaList = kassaList.filter((k) => k.showInExpense);

    useEffect(() => {
        if (!paymentTypeId && visibleTypes.length) setPaymentTypeId(visibleTypes[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleTypes.length]);

    const selectedType = visibleTypes.find((pt) => pt.id === paymentTypeId);
    const cardsForType = selectedType?.isCard ? visibleKassaList.filter((k) => k.paymentTypeId === paymentTypeId) : [];

    const selectedCategory = categories.find((c) => c.id === categoryId);
    const listSources = (selectedCategory?.listSource || "").split(",").filter(Boolean);
    const listSourceKey = listSources.join(",");

    // при выборе категории со списком(ами) — подгружаем реальные имена
    useEffect(() => {
        setPersonSelection("");
        if (!listSourceKey) { setPersonOptions([]); return; }
        setLoadingPersons(true);
        axios.post(`${API_URL}/getPersonnelList`, { listSource: listSourceKey })
            .then(({ data }) => { if (data.status === "success") setPersonOptions(data.names); })
            .catch((err) => console.error("getPersonnelList:", err))
            .finally(() => setLoadingPersons(false));
    }, [listSourceKey, API_URL]);

    const selectedPerson = personOptions.find((p) => `${p.source}::${p.name}` === personSelection) || null;
    const personName = selectedPerson?.name || "";
    const personRole = selectedPerson ? ROLE_BY_LIST_SOURCE[selectedPerson.source] : null;

    const activeCurrencyCode = isPlan
        ? planCurrency
        : (selectedType?.isCard
            ? (visibleKassaList.find((k) => k.id === kassaId)?.currencyCode ?? null)
            : (selectedType?.currencyCode ?? null));

    const submit = async () => {
        const finalTitle = listSourceKey ? personName : title.trim();
        if (!finalTitle) { toast.error(listSourceKey ? "Выберите имя из списка" : "Укажите название расхода"); return; }
        if (!amount || Number(amount) <= 0) { toast.error("Укажите сумму"); return; }
        setSaving(true);
        try {
            const role = listSourceKey ? personRole : null;
            if (isPlan) {
                await axios.post(`${API_URL}/addPlannedExpense`, {
                    title: finalTitle, categoryId, plannedAmount: amount, plannedCurrency: planCurrency, date, comment,
                });
                toast.success("Расход запланирован");
            } else {
                if (!paymentTypeId) { toast.error("Выберите тип оплаты"); setSaving(false); return; }
                if (selectedType?.isCard && !kassaId) { toast.error("Выберите карту"); setSaving(false); return; }
                await axios.post(`${API_URL}/addExpensePayment`, {
                    title: finalTitle, categoryId, role, personName: listSourceKey ? personName : null,
                    paymentTypeId, kassaId: selectedType?.isCard ? kassaId : null,
                    amount, date, comment,
                });
                toast.success("Расход добавлен");
            }
            onSaved();
            onClose();
        } catch (err) {
            console.error("expense submit:", err);
            toast.error("Не удалось сохранить");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="qa-form">
            <div className="qa-till">
                <div className="qa-till-row">
                    <span className="qa-till-label">{isPlan ? "Расход · план" : "Расход"}</span>
                    <span className="qa-till-currency">{activeCurrencyCode || "—"}</span>
                </div>
                <div className="qa-till-amount">−{fmtTill(amount)}</div>
            </div>

            <div className="qa-tear" />

            <div className="qa-switch">
                <button type="button" className={!isPlan ? "active" : ""} onClick={() => setIsPlan(false)}>
                    <i className="fa-solid fa-money-bill-wave"></i> Оплатить сейчас
                </button>
                <button type="button" className={isPlan ? "active" : ""} onClick={() => setIsPlan(true)}>
                    <i className="fa-solid fa-calendar-days"></i> Запланировать
                </button>
            </div>

            <div className="qa-row">
                <div className="qa-field">
                    <label>Категория</label>
                    <select value={categoryId || ""} onChange={(e) => setCategoryId(Number(e.target.value) || null)}>
                        <option value="">— без категории —</option>
                        {mainCats.map((m) => {
                            const subs = subsOf(m.id);
                            return subs.length ? (
                                <optgroup key={m.id} label={m.name}>
                                    {subs.map((s) => <option key={s.id} value={s.id}>{s.name}{s.listSource ? " · список" : ""}</option>)}
                                </optgroup>
                            ) : (
                                <option key={m.id} value={m.id}>{m.name}</option>
                            );
                        })}
                    </select>
                </div>

                {listSourceKey ? (
                    <div className="qa-field">
                        <label>Кому</label>
                        <select value={personSelection} onChange={(e) => setPersonSelection(e.target.value)} disabled={loadingPersons}>
                            <option value="">{loadingPersons ? "Загрузка…" : "— выбрать —"}</option>
                            {personOptions.map((p) => (
                                <option key={`${p.source}::${p.name}`} value={`${p.source}::${p.name}`}>
                                    {p.name}{listSources.length > 1 ? ` · ${LIST_SOURCE_LABELS[p.source] || p.source}` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div className="qa-field">
                        <label>Название</label>
                        <input placeholder="Например: Заправка" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </div>
                )}
            </div>

            {isPlan ? (
                <div className="qa-row">
                    <div className="qa-field">
                        <label>Дата</label>
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                    </div>
                    <div className="qa-field">
                        <label>Валюта</label>
                        <select value={planCurrency} onChange={(e) => setPlanCurrency(e.target.value)}>
                            {currencies.map((c) => <option key={c.id} value={c.code}>{c.code} — {c.name}</option>)}
                        </select>
                    </div>
                </div>
            ) : (
                <>
                    <div className="qa-field">
                        <label>Тип оплаты</label>
                        {visibleTypes.length === 0 ? (
                            <div className="qa-chip-empty">Нет доступных типов — включи хотя бы один в «Расход» на странице Кассы</div>
                        ) : (
                            <div className="qa-chip-row">
                                {visibleTypes.map((pt) => (
                                    <button
                                        type="button"
                                        key={pt.id}
                                        className={`qa-chip${paymentTypeId === pt.id ? " active" : ""}`}
                                        onClick={() => { setPaymentTypeId(pt.id); setKassaId(null); }}
                                    >
                                        {pt.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {selectedType?.isCard && (
                        <div className="qa-field">
                            <label>Карта</label>
                            <div className="qa-chip-row">
                                {cardsForType.map((k) => (
                                    <button
                                        type="button"
                                        key={k.id}
                                        className={`qa-chip${kassaId === k.id ? " active" : ""}`}
                                        onClick={() => setKassaId(k.id)}
                                    >
                                        {k.recipient}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className="qa-field">
                <label>Сумма {activeCurrencyCode ? `(${activeCurrencyCode})` : ""}</label>
                <input type="number" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>

            <div className="qa-field">
                <label>Комментарий <span className="qa-optional">(необязательно)</span></label>
                <textarea rows={2} placeholder="Любая заметка по этому расходу…" value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>

            <button className="qa-submit expense" onClick={submit} disabled={saving}>
                {saving ? "Сохраняем…" : isPlan
                    ? (<><i className="fa-solid fa-calendar-days"></i> Запланировать расход</>)
                    : (<><i className="fa-solid fa-check"></i> Добавить расход</>)}
            </button>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────
 *  Глобальные плавающие кнопки — рендерятся через портал прямо
 *  в <body>, поэтому доступны на любой странице приложения
 * ──────────────────────────────────────────────────────────── */
const QuickActions = ({ API_URL }) => {
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [kassaList, setKassaList] = useState([]);
    const [categories, setCategories] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [incomeOpen, setIncomeOpen] = useState(false);
    const [expenseOpen, setExpenseOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false); // по умолчанию свёрнуто

    const load = useCallback(async () => {
        try {
            const [ptRes, kassaRes, catRes, curRes] = await Promise.all([
                axios.post(`${API_URL}/getPaymentTypes`),
                axios.post(`${API_URL}/getKassaList`),
                axios.post(`${API_URL}/getExpenseCategories`),
                axios.post(`${API_URL}/getCurrencies`),
            ]);
            if (ptRes.data.status === "success") setPaymentTypes(ptRes.data.paymentTypes);
            if (kassaRes.data.status === "success") setKassaList(kassaRes.data.kassaList);
            if (catRes.data.status === "success") setCategories(catRes.data.categories);
            if (curRes.data.status === "success") setCurrencies(curRes.data.currencies);
        } catch (err) {
            console.error("QuickActions load:", err);
        }
    }, [API_URL]);

    useEffect(() => { load(); }, [load]);

    const toggleDrawer = () => {
        setDrawerOpen((v) => {
            if (v) { setIncomeOpen(false); setExpenseOpen(false); } // сворачиваем — заодно закрываем открытые формы
            return !v;
        });
    };

    return createPortal(
        <>
            <div className={`qa-drawer${drawerOpen ? " open" : ""}`}>
                <div className="qa-drawer-buttons">
                    <button className="qa-fab plus" onClick={() => { setIncomeOpen((v) => !v); setExpenseOpen(false); }} title="Прочий приход">
                        <i className="fa-solid fa-plus"></i>
                    </button>
                    <button className="qa-fab minus" onClick={() => { setExpenseOpen((v) => !v); setIncomeOpen(false); }} title="Прочий расход">
                        <i className="fa-solid fa-minus"></i>
                    </button>
                </div>
                <button className="qa-drawer-tab" onClick={toggleDrawer} title={drawerOpen ? "Скрыть" : "Приход / Расход"}>
                    <i className={`fa-solid ${drawerOpen ? "fa-chevron-right" : "fa-chevron-left"}`}></i>
                </button>
            </div>

            {incomeOpen && (
                <div className="qa-overlay" onClick={() => setIncomeOpen(false)}>
                    <div className="qa-modal income" onClick={(e) => e.stopPropagation()}>
                        <button className="qa-modal-close" onClick={() => setIncomeOpen(false)}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                        <div className="qa-modal-icon"><i className="fa-solid fa-sack-dollar"></i></div>
                        <div className="qa-modal-title">Прочий приход</div>
                        <div className="qa-modal-subtitle">Деньги поступили не за заказ — фиксируем вручную</div>
                        <QuickIncomeForm API_URL={API_URL} paymentTypes={paymentTypes} kassaList={kassaList} onSaved={load} onClose={() => setIncomeOpen(false)} />
                    </div>
                </div>
            )}
            {expenseOpen && (
                <div className="qa-overlay" onClick={() => setExpenseOpen(false)}>
                    <div className="qa-modal expense" onClick={(e) => e.stopPropagation()}>
                        <button className="qa-modal-close" onClick={() => setExpenseOpen(false)}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                        <div className="qa-modal-icon"><i className="fa-solid fa-receipt"></i></div>
                        <div className="qa-modal-title">Прочий расход</div>
                        <div className="qa-modal-subtitle">Оплатите сразу или просто запланируйте на потом</div>
                        <QuickExpenseForm API_URL={API_URL} categories={categories} paymentTypes={paymentTypes} kassaList={kassaList} currencies={currencies} onSaved={load} onClose={() => setExpenseOpen(false)} />
                    </div>
                </div>
            )}
        </>,
        document.body
    );
};

export default QuickActions;